import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import App from './App'
import { AuthProvider } from './lib/auth'
import './index.css'

/**
 * Query defaults tuned for clinical data, where the cost of showing something stale is
 * higher than the cost of a refetch.
 *
 * `staleTime` is short and refetch-on-focus is on: a coordinator leaves a tab open for
 * hours while colleagues move candidates through the pipeline, and a stale queue means
 * two people work the same patient. `retry` is 1 rather than the default 3 because the
 * most common failure is "the Python matcher is not running", which will not fix itself
 * and should surface immediately rather than after three backoffs.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: true,
      retry: 1,
    },
    mutations: {
      // Never auto-retry a mutation. Every mutation in this app either transitions a
      // referral or records consent; replaying one silently could double-advance a
      // candidate's state or re-grant a consent the patient just revoked.
      retry: 0,
    },
  },
})

const root = document.getElementById('root')
if (!root) throw new Error('No #root element — check web/index.html.')

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <App />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>
)
